import type { DataSource } from '@unifyapps/defs/types/dataSource';
import { useCallback, useMemo } from 'react';
import DataSourceHelper from '../../../../helper/DataSourceHelper';
import DependencyFlowHelper from '../../../../helper/DependencyFlowHelper';
import useInterfaceEventBus from '../../../../hooks/useInterfaceEventBus';
import { DependencyFlowStatus } from '../../../../slices/DataSourceControllerSlice/types';
import { getIsDataSourceAnEntityType } from '../../../../utils/entityDataSource';
import { ApiEndpointQueryNode } from '../../components/ApiEndpointQueryNode';
import ApplicationDataSourceQueryNode from '../../components/ApplicationDataSourceQueryNode';
import { UAQueryNode } from '../../components/UAQueryNode';
import {
  useDSCActions,
  useDSCDataSourceRecordStore,
  useDSCDataSourcesFromPage,
  useDSCDependencyGraphProvider,
  useDSCEntityTypeDataSources,
  useDSCGetState,
  useDSCGlobalStateStore,
  useDSCPageMetadata,
} from '../../context/selectors';
import useCommitReadyDataSources from '../../hooks/useCommitReadyDataSources';
import useFinishDisabledAutomaticDS from '../../hooks/useFinishDisabledAutomaticDS';
import type { QueryNode } from '../../types';
import { getLookupRequestsForDataSources } from '../../utils/getLookupRequestsForDataSources';

function useAutomaticDataSourceIds() {
  const dependencyFlow = useDSCGlobalStateStore().hooks.dataSource.useDependencyFlow();
  return DependencyFlowHelper.getUnstagedDependencies({ dependencyFlowInstance: dependencyFlow });
}

function AutomaticDataSourceController() {
  const dataSources = useDSCDataSourceRecordStore().hooks.useDataSources();
  const automaticDataSourceIds = useAutomaticDataSourceIds();
  const { disabledDataSources } = useFinishDisabledAutomaticDS(automaticDataSourceIds);
  const { interfaceEventBus } = useInterfaceEventBus();
  const getDataSourceControllerState = useDSCGetState();
  const dataSourcesFromActivePage = useDSCDataSourcesFromPage();
  const metadata = useDSCPageMetadata();
  const { setDataSourceState, addDependencyFlow } = useDSCActions();
  const {
    actions: { notifyDependencyChange },
  } = useDSCDependencyGraphProvider();
  const entityTypeDataSources = useDSCEntityTypeDataSources();

  const dependencies = metadata?.dependencies;
  const dependencyById = metadata?.dependencyById;

  const lookupRequestsByDataSourceId = useMemo(
    () => getLookupRequestsForDataSources(dependencies, dependencyById),
    [dependencies, dependencyById],
  );

  const getDataSource = useCallback(
    (dataSourceId: string) => {
      if (getIsDataSourceAnEntityType(dataSourceId)) {
        const dataSourceRecord = entityTypeDataSources?.[dataSourceId];

        return {
          dataSource: dataSourceRecord ? DataSourceHelper.properties(dataSourceRecord) : undefined,
          version: dataSourceRecord ? DataSourceHelper.version(dataSourceRecord) : undefined,
        };
      }

      return {
        dataSource: dataSources?.[dataSourceId]
          ? DataSourceHelper.properties(dataSources[dataSourceId])
          : dataSourcesFromActivePage?.[dataSourceId],
        version: dataSources?.[dataSourceId]
          ? DataSourceHelper.version(dataSources[dataSourceId])
          : undefined,
      };
    },
    [dataSources, entityTypeDataSources, dataSourcesFromActivePage],
  );

  const getCurrentQueryResult: QueryNode['getCurrentQueryResult'] = useCallback(
    ({ id }) => {
      return getDataSourceControllerState().dataSources[id];
    },
    [getDataSourceControllerState],
  );

  const onQueryCompletion: QueryNode['onQueryCompletion'] = useCallback(
    ({ id, outputSchema }) => {
      addDependencyFlow(id, DependencyFlowStatus.Finished);
      if (outputSchema) {
        interfaceEventBus.publish('onSaveDataSourceSchema', {
          dataSourceId: id,
          outputSchema,
        });
      }
    },
    [addDependencyFlow, interfaceEventBus],
  );

  const onSetDataSourceState: QueryNode['setDataSourceState'] = useCallback(
    ({ id, result }) => {
      setDataSourceState(id, result);
      notifyDependencyChange({ id });
    },
    [notifyDependencyChange, setDataSourceState],
  );

  // This hook is responsible for taking current staged data sources to commit stage when its dependent is finished
  useCommitReadyDataSources();

  return (
    <>
      {automaticDataSourceIds.map((id) => {
        const { dataSource, version } = getDataSource(id);

        const lookupRequests = lookupRequestsByDataSourceId[id];

        const dependsOn = DataSourceHelper.getDependsOnFromProperties(dataSource);
        const dynamicPaths = DataSourceHelper.getDynamicPathsFromProperties(dataSource);

        if (!dataSource) {
          console.warn(`Data source with id ${id} not found`);
          return null;
        }

        if (dataSource.type === 'QUERY_BY_UA' && dataSource.inputs) {
          return (
            <UAQueryNode
              callbacks={dataSource.callbacks}
              dependsOn={dependsOn}
              dynamicPaths={dynamicPaths}
              id={id}
              key={id}
              onQueryCompletion={onQueryCompletion}
              query={dataSource.inputs}
              requestId={id}
            />
          );
        } else if (dataSource.type === 'API_ENDPOINT' && dataSource.inputs) {
          return (
            <ApiEndpointQueryNode
              apiEndpoint={dataSource.inputs}
              callbacks={dataSource.callbacks}
              id={id}
              key={id}
              onQueryCompletion={onQueryCompletion}
              requestId={id}
            />
          );
        } else if (dataSource.type === 'APPLICATION') {
          return (
            <ApplicationDataSourceQueryNode
              dataSource={dataSource}
              dependsOn={dependsOn}
              disabled={disabledDataSources[id]}
              dynamicPaths={dynamicPaths}
              getCurrentQueryResult={getCurrentQueryResult}
              id={id}
              key={id}
              lookupRequests={lookupRequests}
              onQueryCompletion={onQueryCompletion}
              originalDataSourceId={dataSource.standardDataSourceId ?? id}
              requestId={id}
              setDataSourceState={onSetDataSourceState}
              version={version}
            />
          );
        }
        return null;
      })}
    </>
  );
}

export default AutomaticDataSourceController;
